type FormValue = string | number | boolean | File | TFormObject | FormArray | null | undefined;

export type TFormObject = { [key: string]: FormValue };

type FormArray = FormValue[];

/**
 * Converts a plain object into a `FormData` instance, recursively handling nested objects,
 * arrays, files, and primitives. Nested object keys use dot notation (`parent.child`),
 * array indices use bracket notation (`list[0]`).
 */
export function formGroupToFormData<T extends TFormObject>(formValue: T, rootKey = ''): FormData {
  const formData = new FormData();
  processFormValue(formValue, rootKey, formData);
  return formData;
}

function processFormValue(value: FormValue, parentKey: string, formData: FormData): void {
  if (value === null || value === undefined) return;
  if (isFile(value)) processFile(value, parentKey, formData);
  else if (isArray(value)) processArray(value, parentKey, formData);
  else if (isObject(value)) processObject(value, parentKey, formData);
  else processPrimitive(value, parentKey, formData);
}

function processFile(file: File, key: string, formData: FormData): void {
  formData.append(key, file);
}

function processArray(array: FormValue[], parentKey: string, formData: FormData): void {
  array.forEach((item, index) => {
    const newKey = `${parentKey}[${index}]`;
    if (item === null || item === undefined) formData.append(newKey, '');
    else processFormValue(item, newKey, formData);
  });
}

function processObject(obj: TFormObject, parentKey: string, formData: FormData): void {
  Object.entries(obj).forEach(([key, value]) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    processFormValue(value, newKey, formData);
  });
}

function processPrimitive(value: string | number | boolean, key: string, formData: FormData): void {
  formData.append(key, value.toString());
}

function isFile(value: FormValue): value is File {
  return value instanceof File;
}

function isArray(value: FormValue): value is FormArray {
  return Array.isArray(value);
}

function isObject(value: FormValue): value is TFormObject {
  return typeof value === 'object' && value !== null && !(value instanceof File) && !Array.isArray(value);
}
